import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  type ThHTMLAttributes,
  type TdHTMLAttributes,
} from 'react';
import { cx } from '@/lib/cx';

/*
 * Table parts rather than a generic DataTable: every surface needs slightly
 * different cells, and a config-driven table ends up more code, not less.
 * The wrapper owns horizontal scrolling so a wide table never widens the page.
 */
export function TableWrap({
  className,
  children,
  frozenHeader = false,
  frozenReservePx = 72,
  frozenMaxHeight,
}: {
  className?: string;
  children: ReactNode;
  /**
   * The table scrolls inside its own area, which fills the viewport from the
   * wrapper's top edge down, and every column header stays pinned at the top
   * of that area. The horizontal scrollbar is then the area's own bottom edge.
   *
   * ⚠️ **Without it the wrapper is the plain horizontal scroller it always
   * was** — a separate component below, so nothing about the default changes.
   */
  frozenHeader?: boolean;
  /** Space kept below the area for the pager, in px. Default 72. */
  frozenReservePx?: number;
  /**
   * A fixed CSS max-height for the area (e.g. `'min(60vh, 560px)'`), used
   * instead of measuring. For a table inside a Modal, card, side panel or a
   * dashboard with several tables, where "fill the viewport below me" is wrong.
   * Only read with `frozenHeader`; `frozenReservePx` is then ignored.
   */
  frozenMaxHeight?: string;
}) {
  if (!frozenHeader) {
    // Resets the context: a plain table nested inside a frozen one (an expanded
    // row's matrix) must not inherit sticky headers that paint over the outer one.
    return (
      <FrozenHeaderContext.Provider value={false}>
        <div className={cx('w-full overflow-x-auto', className)}>{children}</div>
      </FrozenHeaderContext.Provider>
    );
  }
  return (
    <FrozenTableWrap className={className} reservePx={frozenReservePx} fixedMaxHeight={frozenMaxHeight}>
      {children}
    </FrozenTableWrap>
  );
}

/**
 * Whether the cells below sit inside a frozen-header wrapper. Read by `TH` to
 * make every column header sticky to the top of the scroll area.
 *
 * ⚠️ **Context, not a prop on every `TH`.** A page has dozens of header cells,
 * several built by `FilterableTH`; threading a flag through each is how one
 * header ends up scrolling away while its neighbours stay put.
 */
const FrozenHeaderContext = createContext(false);

/** Never shorter than this, so a short viewport still shows a usable table. */
const FROZEN_MIN_HEIGHT_PX = 320;

/**
 * Never shorter than this share of the viewport either, so a tall page header
 * cannot squeeze the area down to a few rows: a table further down the page
 * keeps ~70% of the screen and the PAGE scrolls to bring it into view.
 */
const FROZEN_MIN_VIEWPORT_SHARE = 0.7;

/** The app's sticky top bar. The area is never taller than the space under it. */
const FROZEN_TOP_BAR_PX = 64;

function FrozenTableWrap({
  className,
  reservePx,
  fixedMaxHeight,
  children,
}: {
  className?: string;
  reservePx: number;
  fixedMaxHeight: string | undefined;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  /*
   * Measured from the wrapper's position in the DOCUMENT, not in the viewport:
   * the window still scrolls the page, and a height that changed as the reader
   * scrolled would make the table resize under the wheel. Re-measured whenever
   * the window resizes or the page's own height changes — a filter bank
   * opening above the table moves its top without resizing the wrapper.
   */
  useLayoutEffect(() => {
    // A fixed height needs no measuring at all.
    if (fixedMaxHeight !== undefined) return;
    const element = ref.current;
    if (!element) return;
    let frame = 0;
    const measure = () => {
      const viewport = window.innerHeight;
      const top = element.getBoundingClientRect().top + window.scrollY;
      const next = Math.min(
        viewport - FROZEN_TOP_BAR_PX,
        Math.max(
          FROZEN_MIN_HEIGHT_PX,
          Math.floor(viewport - top - reservePx),
          Math.round(FROZEN_MIN_VIEWPORT_SHARE * viewport),
        ),
      );
      setMaxHeight((current) => (current === next ? current : next));
    };
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('resize', schedule);
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(document.body);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [reservePx, fixedMaxHeight]);

  return (
    <FrozenHeaderContext.Provider value>
      <div
        ref={ref}
        /*
         * `relative` is load-bearing. Without it the wrapper is not a containing
         * block, so every absolutely positioned descendant — the `sr-only`
         * labels in each row — escapes the scroll box's clip and stretches the
         * PAGE to the table's full height: rows clipped away inside this box
         * still made ~1,000px of empty space below it (/companies, 2026-10-04).
         */
        /*
         * Only ever a MAX-height: content shorter than the cap sizes the box
         * itself, so it looks exactly like the plain wrapper — no spare space
         * and no inner scrollbar.
         */
        className={cx('relative w-full overflow-auto', className)}
        style={
          fixedMaxHeight !== undefined
            ? { maxHeight: fixedMaxHeight }
            : maxHeight === null
              ? undefined
              : { maxHeight }
        }
      >
        {children}
      </div>
    </FrozenHeaderContext.Provider>
  );
}

/**
 * ⚠️ **`style` is accepted because a table's minimum width is now COMPUTED.**
 * Hiding a column must shrink the table's floor, and Tailwind v4 scans source
 * text — `min-w-[${n}px]` emits no rule, so the number has nowhere to go but an
 * inline style. Without this the first three adopting pages each wrapped the
 * table in a spacer `<div>` to carry it, which is three copies of a workaround
 * for a missing prop.
 */
export function Table({
  className,
  style,
  children,
}: {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <table style={style} className={cx('w-full border-collapse text-left text-sm', className)}>
      {children}
    </table>
  );
}

export function THead({ className, children }: { className?: string; children: ReactNode }) {
  return <thead className={cx('bg-surface-sunken', className)}>{children}</thead>;
}

export function TBody({ className, children }: { className?: string; children: ReactNode }) {
  return <tbody className={className}>{children}</tbody>;
}

/*
 * Pinning a column to the right edge of the scroller.
 *
 * `TableWrap` scrolls horizontally, so on a wide table the last column — which
 * on every list page is the row actions — sits off the right edge of the
 * scroller until somebody drags the table sideways, and nothing signals that
 * they should. Measured on `/companies` — then a table whose floor was a
 * hand-written 1328px min-width class, in a content area of viewport minus a
 * ~256px sidebar: the Actions cell was 106px past the edge at 1440 and 266px
 * past it at 1280, so the create menu, edit and delete were simply unreachable.
 * `pinned` makes the cell `sticky right-0`, so it stays put while the rest of
 * the table scrolls under it.
 *
 * ⚠️ *SUPERSEDED 2026-09-14, the MECHANISM and not the measurement: that
 * parenthesis read "a 1328px table", with the width spelled out as a complete
 * arbitrary-value utility.* **The measurement stands as the REASON `pinned`
 * exists** — a wide table's actions lane really was unreachable at 1280 and
 * 1440, and still would be without this. Two things about the sentence changed:
 *
 * - **`/companies` carries no such literal now.** Its floor is SUMMED IN
 *   JAVASCRIPT from the visible columns' pixel widths — one `COLUMN_WIDTHS`
 *   record in `CompaniesPage.tsx`, applied through `<Table style>`, which is why
 *   `Table` accepts `style` at all (see the note on it above). The page has
 *   since grown well past the column set that sum was cut for, and the floor now
 *   MOVES as columns are hidden, so **no single number describes that table any
 *   more**: 1328 was a reading taken on a day, not a property of the page. Read
 *   `COLUMN_WIDTHS`; never quote a width from here.
 * - ⛔ **The old spelling was EMITTING the very rule it documented.** Tailwind v4
 *   scans SOURCE TEXT, comments included, so a complete arbitrary-value class
 *   written in prose put a width nothing uses into the production stylesheet —
 *   and defeated anyone grepping the served bundle to prove it had gone.
 *   `COLUMN_WIDTHS`'s own doc breaks that literal up for this reason and says it
 *   is spelled out "HERE AND NOWHERE ELSE"; it was not, and this was the other
 *   place. **Never write a complete Tailwind class in a comment.**
 *
 * ⛔ **NEITHER BULLET RETIRES THE HAND-WRITTEN FLOOR. IT IS STILL THE RULE ON
 * THE THREE LINE GRIDS, AND THE TWO TABLES USE TWO DIFFERENT MECHANISMS.** The
 * difference is `table-fixed`:
 *
 * - The quote, invoice and deal line grids **are** fixed-layout with a
 *   `<colgroup>`, and their floor is a hand-written `min-w-` arbitrary value
 *   that must be moved BY HAND together with the colgroup widths and the
 *   per-cell content minimums — three lists that can silently drift apart, which
 *   is why each of those grids states the sum beside the value it must equal.
 * - `/companies` is **not** fixed-layout and its floor is computed, which is the
 *   whole reason it could stop carrying a literal at all.
 *
 * ⚠️ **Ask which kind of table you are in before moving a width.** A reader who
 * takes the correction above as a general retirement will leave a grid's sum
 * behind the column it belongs to — and under `table-fixed` that is a silently
 * mis-laid-out grid rather than an error.
 *
 * Four details are load-bearing:
 *
 * - **The hairlines are INSET SHADOWS, not borders.** `Table` is
 *   `border-collapse: collapse`, where a border belongs to the table rather
 *   than to the cell: it is painted at the grid line and does NOT travel with a
 *   sticky cell, so a `border-l` here draws a stray vertical rule down the
 *   middle of the table instead of an edge on the pinned column. They are still
 *   1px hairlines in a token colour, which is the house style; only the
 *   mechanism differs, because the house mechanism cannot express it here.
 * - **The pinned body cell REDRAWS the row separator.** Same collapse rule from
 *   the other side: a positioned cell paints above the table's collapsed
 *   borders, so `TR`'s own `border-b` disappears underneath the pinned column.
 *   `group-last/row` drops it on the last row, matching `TR`'s
 *   `last:border-b-0` — otherwise the table would end on a stub of hairline
 *   under the pinned column alone.
 * - **The background must be opaque**, or the scrolling content reads straight
 *   through the pinned cell. It is written out rather than inherited: a `<tr>`
 *   has no background of its own to inherit, and giving every row one would
 *   repaint the tables that sit on `surface-raised` inside a modal.
 * - **Hover is driven by the ROW.** A `<td>` carrying its own background does
 *   not pick up `TR interactive`'s `hover:bg-surface-hover`, and would sit
 *   unhighlighted while the rest of the row lit up. `TR` names itself
 *   `group/rowhover` when it is interactive and the pinned cell answers that,
 *   so the highlight is still one decision made in one place. The name is
 *   applied ONLY when interactive, so a skeleton row cannot gain a hover state
 *   its other cells do not have.
 *
 * `var(--hairline)` and not `var(--color-border)`: the `--color-*` names live
 * in `@theme inline`, which inlines them into utilities instead of emitting
 * them, so they do not exist at runtime. `--hairline` is the real property, and
 * it is the one the dark theme overrides — no `dark:` variant needed here.
 *
 * ⚠️ **`z-20` is the CEILING for page content, not a spare number.** A pinned
 * cell is `position: sticky` with a `z-index`, so it is a stacking context in
 * the page's own root — the same root the app's sticky top bar sits in, and the
 * table is always further down the document than the bar. While the bar was
 * also `z-20` the equal z-index was settled by document order and this header
 * cell painted straight over an open account menu, on every list page at once.
 * The bar now sits a rung higher; see the ladder in `src/index.css`. Do not
 * raise either of these two values to win a fight with something on the page —
 * that re-opens the same collision from the other side.
 */
const PINNED_TH = 'sticky right-0 z-20 bg-surface-sunken shadow-[inset_1px_0_0_0_var(--hairline)]';

const PINNED_TD =
  'sticky right-0 z-10 bg-surface shadow-[inset_1px_0_0_0_var(--hairline),inset_0_-1px_0_0_var(--hairline)] group-last/row:shadow-[inset_1px_0_0_0_var(--hairline)] group-hover/rowhover:bg-surface-hover transition-colors duration-150 ease-out motion-reduce:transition-none';

/**
 * Which edge a frozen column sticks to.
 *
 * ⚠️ **`pinned` IS THE ONE-COLUMN SPECIAL CASE OF THIS, AND IT STAYS.** Fifteen
 * pages pass `pinned` on their actions cell today; it is exactly
 * `frozen="right"` with a zero offset, so it is kept as the shorthand rather
 * than migrated — a sweep of fifteen files to express the same thing differently
 * is churn that can only introduce regressions.
 */
export type FrozenSide = 'left' | 'right';

/*
 * ── FREEZING SEVERAL COLUMNS, ON EITHER EDGE ───────────────────────────────
 *
 * Everything `pinned` says above applies here — the inset shadows, the redrawn
 * row separator, the opaque background, the `z-20` ceiling. What follows is the
 * two things that are different once more than ONE column can freeze, and both
 * of them are constraints this codebase has already paid for elsewhere.
 *
 * **1. THE OFFSET MUST BE AN INLINE STYLE, NEVER A CLASS.** `pinned` can say
 * `right-0` because the pinned column is always the last one. The second frozen
 * column has to sit at the summed width of the columns outside it, which is only
 * known at runtime — and ⛔ **Tailwind v4 scans SOURCE TEXT, so `left-[${n}px]`
 * emits no rule at all.** The class lands in the DOM and does nothing, which
 * looks exactly like a column that simply chose not to stick.
 * `UniversityPartnershipsPage` records the same trap against a computed
 * `min-w-[…]`, and solves it the same way: a number goes through `style`.
 *
 * **2. THERE ARE FOUR SHADOW LITERALS AND THEY MAY NOT BE COMPOSED.**
 * 🔴 `box-shadow` is ONE property, so two `shadow-[…]` utilities on one element
 * do not merge — one wins, and which one is whatever order Tailwind happened to
 * emit them in. A frozen body cell needs its EDGE and the ROW SEPARATOR it has
 * to redraw for itself, in a single declaration. `RevenueTrackerPage` hit this
 * head-on and ended up hand-writing six whole literals for ONE frozen column;
 * four constants here cover every column on either edge, because the background
 * is the only other variable and it is already a class.
 *
 * ⚠️ **The edge flips sign with the side.** A left-frozen cell draws its rule on
 * its RIGHT (`inset_-1px`), a right-frozen cell on its LEFT (`inset_1px`) — the
 * rule always faces the content that scrolls under it.
 *
 * ⚠️ **A left-frozen cell covers what the reader has already scrolled PAST**,
 * which is ordinary frozen-pane behaviour and is the point. A right-frozen one
 * floats over cells they are scrolling TOWARDS, which is why the actions lane
 * has produced a report of buttons drawn over a live column. Freeze-left is the
 * safer edge; do not assume the two are symmetrical in use just because they are
 * symmetrical here.
 */
const FROZEN_TH_LEFT =
  'sticky z-20 bg-surface-sunken shadow-[inset_-1px_0_0_0_var(--hairline)]';

const FROZEN_TH_RIGHT = 'sticky z-20 bg-surface-sunken shadow-[inset_1px_0_0_0_var(--hairline)]';

/*
 * ── THE FROZEN HEADER ROW (`TableWrap frozenHeader`) ─────────────────────
 *
 * Every column header becomes `sticky top-0` inside the wrapper's own scroll
 * area. Three things are load-bearing, and all three are the same rules as the
 * frozen columns above:
 *
 * - **The bottom hairline is an inset shadow.** `TR`'s `border-b` is a
 *   collapsed border that belongs to the table, so it stays where the row was
 *   while the cell moves — the header would float with no rule under it.
 * - **So the edge cells need COMBINED literals.** A pinned or frozen header
 *   cell already draws a vertical rule with `box-shadow`, and two shadow
 *   utilities do not merge. Each sticky variant below carries both rules in one
 *   declaration, written out whole.
 * - **An ordinary header cell sits at `z-[15]`, NOT `z-20`.** Equal z-index is
 *   settled by document order, and a left-frozen header cell comes BEFORE the
 *   cells that scroll under it — at a shared `z-20` they would paint over it.
 *   15 keeps them above the pinned BODY cells (10) that scroll up beneath them
 *   and below every frozen or pinned header cell (20), inside the page-content
 *   band the ladder allows.
 */
const STICKY_TH = 'sticky top-0 z-[15] bg-surface-sunken shadow-[inset_0_-1px_0_0_var(--hairline)]';

const PINNED_TH_STICKY =
  'sticky top-0 right-0 z-20 bg-surface-sunken shadow-[inset_1px_0_0_0_var(--hairline),inset_0_-1px_0_0_var(--hairline)]';

const FROZEN_TH_LEFT_STICKY =
  'sticky top-0 z-20 bg-surface-sunken shadow-[inset_-1px_0_0_0_var(--hairline),inset_0_-1px_0_0_var(--hairline)]';

const FROZEN_TH_RIGHT_STICKY =
  'sticky top-0 z-20 bg-surface-sunken shadow-[inset_1px_0_0_0_var(--hairline),inset_0_-1px_0_0_var(--hairline)]';

const FROZEN_TD_LEFT =
  'sticky z-10 bg-surface shadow-[inset_-1px_0_0_0_var(--hairline),inset_0_-1px_0_0_var(--hairline)] group-last/row:shadow-[inset_-1px_0_0_0_var(--hairline)] group-hover/rowhover:bg-surface-hover transition-colors duration-150 ease-out motion-reduce:transition-none';

const FROZEN_TD_RIGHT =
  'sticky z-10 bg-surface shadow-[inset_1px_0_0_0_var(--hairline),inset_0_-1px_0_0_var(--hairline)] group-last/row:shadow-[inset_1px_0_0_0_var(--hairline)] group-hover/rowhover:bg-surface-hover transition-colors duration-150 ease-out motion-reduce:transition-none';

/**
 * The inline offset for a frozen cell.
 *
 * Returns `undefined` when the cell is not frozen, so the attribute is absent
 * rather than set to a falsy value — an unfrozen cell must carry no `left`/
 * `right` at all, or it becomes a positioned cell that paints over the table's
 * collapsed borders for no reason.
 */
function frozenStyle(
  frozen: FrozenSide | undefined,
  offset: number | undefined,
): { left: number } | { right: number } | undefined {
  if (frozen === undefined) return undefined;
  return frozen === 'left' ? { left: offset ?? 0 } : { right: offset ?? 0 };
}

export interface TRProps extends HTMLAttributes<HTMLTableRowElement> {
  /**
   * Declared explicitly because `HTMLAttributes` doesn't include it, exactly as
   * `Button` does.
   *
   * 🔴 **Added 2026-09-12 because freezing needs to MEASURE the header row.**
   * Column offsets are the summed rendered widths of the cells outside the
   * frozen one, and in an auto-layout table that is only knowable from the DOM.
   * Without a ref here the first adopting page hand-rolled a bare `<tr>` and
   * copied TR's classes into it — a second copy of this component's styling
   * that nothing would keep in step. React 19 passes `ref` through as an
   * ordinary prop, so spreading `...rest` is enough.
   */
  ref?: Ref<HTMLTableRowElement>;
  /** Row-level hover affordance for clickable rows. */
  interactive?: boolean;
  children: ReactNode;
}

export function TR({ className, interactive, children, ...rest }: TRProps) {
  return (
    <tr
      className={cx(
        // Two named groups, both only read by a `pinned` cell: `row` so it can
        // tell whether it is in the last row, `rowhover` so it only follows a
        // hover the row actually has. Named rather than a bare `group` so they
        // cannot collide with a group inside a cell.
        'group/row border-b border-border last:border-b-0',
        interactive &&
          'group/rowhover transition-colors duration-150 ease-out hover:bg-surface-hover motion-reduce:transition-none',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

export interface THProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Right-aligns and tabular-aligns numeric columns. */
  numeric?: boolean;
  /** Pins the column to the right edge of the scroller — see the note above. */
  pinned?: boolean;
  /** Freezes this column to an edge. See the freezing note above. */
  frozen?: FrozenSide;
  /**
   * Distance in px from that edge — the summed widths of the frozen columns
   * OUTSIDE this one. Measured at runtime and applied as an inline style,
   * because a computed Tailwind class emits nothing.
   */
  frozenOffset?: number;
  /**
   * Lets a column heading WRAP onto a second line instead of running past its
   * column.
   *
   * ⚠️ **This is a prop rather than a class at the call site because it swaps a
   * baked-in one**, and the header comment below says why that cannot be done
   * from `className`: `whitespace-nowrap` and `whitespace-normal` are the same
   * property, so which wins is decided by the emitted stylesheet's order and
   * not by the order they are written in. Here exactly one of the two is ever
   * emitted, so there is nothing to race.
   *
   * 🔴 **WHAT IT FIXES, reported 2026-09-03**: under `table-fixed` a heading
   * wider than its `<col>` does not truncate and does not widen the column — it
   * PAINTS OVER ITS NEIGHBOUR. "Awaiting signature" ran across "Your last note"
   * on the University Partnerships quotes card, and the two headings were
   * legible enough that the row read as one long word rather than as a fault.
   * **Nothing overflows, nothing errors and no layout breaks**, which is why it
   * survived a browser pass: it is only wrong to a reader.
   *
   * Default `false`, so every existing header is unchanged.
   */
  wrap?: boolean;
}

/*
 * Column headers and row headers want opposite type: a column header is a small
 * muted sentence-case label, a row header is body text that happens to be a `<th>`.
 * Passing `scope="row"` switches to the latter — otherwise call sites end up
 * fighting the baked-in classes with `!` overrides, since same-property Tailwind
 * utilities don't resolve by string order.
 */
export function TH({
  numeric,
  pinned,
  frozen,
  frozenOffset,
  wrap,
  className,
  style,
  children,
  scope = 'col',
  ...rest
}: THProps) {
  const isRowHeader = scope === 'row';
  // A row header is a BODY cell, so it never sticks to the top.
  const stickyTop = useContext(FrozenHeaderContext) && !isRowHeader;
  return (
    <th
      scope={scope}
      // The caller's own style wins the merge, so a page that positions a cell
      // itself is not silently overruled by a freeze it did not ask for.
      style={{ ...frozenStyle(frozen, frozenOffset), ...style }}
      className={cx(
        'px-3',
        isRowHeader
          ? 'py-2.5 text-sm font-medium text-fg-secondary'
          : cx(
              // Sentence case, no capitals and no tracking — owner decision
              // 2026-10-11. One step larger than the old small-caps label,
              // which sentence case more than pays for in width.
              'py-2 text-xs font-medium text-fg-muted',
              // Exactly one of the pair, decided here — see `wrap`.
              wrap ? 'whitespace-normal' : 'whitespace-nowrap',
            ),
        numeric && 'text-right',
        // Above the pinned body cells, so a future sticky header still wins —
        // and no higher: `z-20` is the top of the page-content band.
        // Exactly one positioning literal per cell — see the frozen-header note.
        stickyTop && !pinned && frozen === undefined && STICKY_TH,
        pinned && (stickyTop ? PINNED_TH_STICKY : PINNED_TH),
        /*
         * ⚠️ **A `scope="row"` CELL IS IN THE BODY, SO IT FREEZES LIKE A `TD`.**
         * Several tables make their first column a row header, and that column
         * is the natural freeze-left candidate — so this branch is the common
         * case, not an edge. Given the header's literals it would take the
         * sunken background, `z-20` and no redrawn row separator: a body cell
         * painted as a heading, sitting a rung above every other frozen body
         * cell. It looks like a styling slip and is really a stacking-order one.
         */
        frozen === 'left' &&
          (isRowHeader ? FROZEN_TD_LEFT : stickyTop ? FROZEN_TH_LEFT_STICKY : FROZEN_TH_LEFT),
        frozen === 'right' &&
          (isRowHeader ? FROZEN_TD_RIGHT : stickyTop ? FROZEN_TH_RIGHT_STICKY : FROZEN_TH_RIGHT),
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export interface TDProps extends TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
  /** Pins the column to the right edge of the scroller — see the note above. */
  pinned?: boolean;
  /** Freezes this column to an edge. See the freezing note above. */
  frozen?: FrozenSide;
  /** px from that edge — the summed widths of the frozen columns outside this one. */
  frozenOffset?: number;
  /**
   * Where the cell's content sits when the row is taller than it is.
   *
   * `'middle'` (the default, and every existing call site) is right while every
   * cell is one or two lines. **A row carrying a WRAPPED PARAGRAPH needs
   * `'top'` on the whole row**: centred, a four-line note starts below the name
   * of the record it describes and ends below the money, so no two cells on the
   * row line up.
   *
   * ⚠️ **A prop, not a class, for the same reason as `TH`'s `wrap`.**
   * `align-middle` is baked in above, and `align-middle` / `align-top` are the
   * same CSS property, so an `align-top` passed through `className` is racing
   * the emitted stylesheet's order rather than overriding anything. Exactly one
   * is emitted here. This file's own `TH` comment records the same trap, and
   * `UniversityPartnershipsPage` records it twice more against `Button`.
   *
   * ⚠️ **Named `verticalAlign` and NOT `align`, which `tsc` refused**: `align`
   * is a real (deprecated) HTML attribute on `<td>` and is already declared by
   * `TdHTMLAttributes` as the HORIZONTAL one, so the narrower type would have
   * been an incompatible override of a prop that also gets spread onto the DOM.
   */
  verticalAlign?: 'middle' | 'top';
}

export function TD({
  numeric,
  pinned,
  frozen,
  frozenOffset,
  verticalAlign = 'middle',
  className,
  style,
  children,
  ...rest
}: TDProps) {
  return (
    <td
      style={{ ...frozenStyle(frozen, frozenOffset), ...style }}
      className={cx(
        // Body text at full contrast; the header row carries the muted step.
        'px-3 py-2.5 text-fg',
        verticalAlign === 'top' ? 'align-top' : 'align-middle',
        numeric && 'text-right',
        pinned && PINNED_TD,
        frozen === 'left' && FROZEN_TD_LEFT,
        frozen === 'right' && FROZEN_TD_RIGHT,
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}
